import {
	PatientEntityType,
	SupabaseShareEntityViewEnum,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { StateCreator } from "zustand";
import { useCentralStore } from "../Central";
import { Logger } from "@/lib/logger/Logger";
import { supabase } from "@/lib/supabase";
import { createSecurePatientName } from "../../lib/utils/utils-functions";
import { showNotification } from "../Central/selectors";
import { PatientSearchResult } from "../Search/types";

/* Checks if the patient is a PatientEntityType, i.e. has all information
 * as opposed to LazyPatient, which only fetches the minimum information lazily.
 */
function isLazyPatient(patient: LazyPatient | PatientEntityType): boolean {
	return (patient as LazyPatient).patient_id !== undefined;
}

function getLazyPatientLookup(
	data: JobsWithPatientResult[] | PatientSearchResult[]
): Record<string, LazyPatient> {
	return data.reduce(
		(acc: Record<string, LazyPatient>, patient) => {
			if (!patient) {
				return acc;
			}
			if ("patient_id" in patient && patient.patient_id) {
				// Case where the patient is a LazyPatient
				acc[patient.patient_id] = {
					patient_id: patient.patient_id,
					first_name: patient.first_name ?? "",
					last_name: patient.last_name ?? "",
					code: patient?.code ?? "",
					title: patient?.title ?? "",
				};
			} else if ("id" in patient && patient.id) {
				// Case where the patient is a PatientSearchResult
				acc[patient.id] = {
					patient_id: patient.id,
					first_name: patient?.first_name ?? "",
					last_name: patient?.last_name ?? "",
					code: patient?.code ?? "",
					title: patient?.title ?? "",
				};
			}
			return acc;
		},
		{} as Record<string, LazyPatient>
	);
}

interface JobsWithPatientResult {
	client_id: string | null;
	code: string | null;
	first_name: string | null;
	last_name: string | null;
	patient_id: string | null;
	title: string | null;
}

export interface LazyPatient {
	patient_id: string;
	first_name: string;
	last_name: string;
	code: string;
	title: string;
}

/**
 * - fetchPatients is called whenever a client is opened for all patients of that client to improve performance.
 * - getPatient is called whenever a patient is opened to fetch the patient data.
 */
export interface PatientStoreSlice {
	patientsLookup: Record<string, LazyPatient | PatientEntityType>;
	fetchedClientsCache: Record<string, boolean>;
	pinnedPatients: Record<string, PatientEntityType>;
	fetchPatients: (clientId: string) => Promise<void>;
	fetchPatientSearchResults: (query: string) => Promise<LazyPatient[] | null>;
	fetchPatientsByIds: (patientIds: string[]) => Promise<void>;
	getPatient: (patientId: string) => Promise<PatientEntityType | null>;
	upsertPatient: (patient: PatientEntityType) => void;
	patientName: (patient: LazyPatient | PatientEntityType) => string;
	createPatientFromName: (name: string) => Promise<string | null>;
	pinPatient: (patient: PatientEntityType) => void;
	unpinPatient: (patientId: string) => void;
	unpinAllPatients: () => void;
}

export const createPatientStore: StateCreator<PatientStoreSlice> = (
	set,
	get
) => ({
	patientsLookup: {},
	fetchedClientsCache: {},
	pinnedPatients: {},
	fetchPatients: async (clientId: string) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization ID");
			return;
		}

		if (get().fetchedClientsCache[clientId]) {
			return;
		}

		let allData: JobsWithPatientResult[] = [];
		let hasMore = true;
		let page = 0;
		const pageSize = 1000;

		while (hasMore) {
			const { data, error } = await supabase
				.from(SupabaseShareEntityViewEnum.JOBS_WITH_PATIENT)
				.select()
				.eq("client_id", clientId)
				.range(page * pageSize, (page + 1) * pageSize - 1);

			if (error) {
				Logger.error(error);
				return;
			}

			if (data) {
				allData = [...allData, ...data];
				hasMore = data.length === pageSize;
				page++;
			} else {
				hasMore = false;
			}
		}

		// Merge the lazy patients lookup with the existing patients lookup; where the existing patients lookup takes precedence
		// because it has more data
		set((state) => ({
			patientsLookup: {
				...getLazyPatientLookup(allData),
				...state.patientsLookup,
			},
		}));
		set((state) => ({
			fetchedClientsCache: {
				...state.fetchedClientsCache,
				[clientId]: true,
			},
		}));
	},
	fetchPatientSearchResults: async (query: string) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			return null;
		}
		const { data, error } = await supabase.rpc("search_patients", {
			query: query,
			p_organization_id: organizationId,
		});

		if (error) {
			Logger.error(error);
			return null;
		}
		const lazyPatientsLookup = getLazyPatientLookup(data);
		set((state) => ({
			patientsLookup: {
				...lazyPatientsLookup,
				...state.patientsLookup,
			},
		}));

		return Object.values(lazyPatientsLookup);
	},
	fetchPatientsByIds: async (patientIds: string[]) => {
		if (patientIds.length === 0) {
			return;
		} else if (patientIds.length > 1000) {
			showNotification({
				message: "Es wurden mehr als 1000 Patienten ausgewählt.",
				type: "warning",
			});
			return;
		}

		const { data, error } = await supabase
			.from("patients")
			.select()
			.in("id", patientIds);

		if (error) {
			Logger.error(error);
			return;
		}

		const newPatientsLookup = data.reduce(
			(acc, patient) => {
				if (patient.id) {
					acc[patient.id] = patient;
				}
				return acc;
			},
			{} as Record<string, PatientEntityType>
		);

		set((state) => ({
			patientsLookup: {
				...state.patientsLookup,
				...newPatientsLookup,
			},
		}));
	},
	fetchPatientsForMonthlyInvoices: async (clientId: string) => {
		await get().fetchPatients(clientId);
	},
	getPatient: async (patientId: string) => {
		const patientsLookup = get().patientsLookup;
		const existingPatient = patientsLookup[patientId];

		if (existingPatient && !isLazyPatient(existingPatient)) {
			return existingPatient as PatientEntityType;
		} else {
			const { data, error } = await supabase
				.from("patients")
				.select()
				.eq("id", patientId)
				.single();

			if (error) {
				Logger.error(error);
				return null;
			}

			set((state) => ({
				patientsLookup: {
					...state.patientsLookup,
					[patientId]: data,
				},
			}));

			return data;
		}
	},
	upsertPatient: (patient) => {
		if (!patient.id) {
			return;
		}
		set((state) => ({
			patientsLookup: {
				...state.patientsLookup,
				[patient.id as string]: {
					...patient,
				},
			},
		}));
	},
	patientName: (patient) => {
		const isPrivacyOn =
			!!useCentralStore.getState().profile?.data_privacy_mode;

		return createSecurePatientName(
			(patient as PatientEntityType)?.id ??
				(patient as LazyPatient)?.patient_id ??
				null,
			patient?.first_name ?? "",
			patient?.last_name ?? "",
			isPrivacyOn
		);
	},
	createPatientFromName: async (name: string) => {
		if (!name) {
			return null;
		}
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			return null;
		}
		const firstName = name?.split(" ")[0];
		const lastName = name?.split(" ")[1];
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PATIENTS)
			.insert({
				first_name: firstName,
				last_name: lastName,
				organization_id: organizationId,
				code: "",
			})
			.select()
			.single();
		if (error) {
			Logger.error(error);
			return null;
		}
		const patientId = data.id;
		if (!patientId) {
			return null;
		}
		// Because the patient entity doesn't sufficiently overlap with patient with share
		// we add them as a lazy patient
		set((state) => ({
			patientsLookup: {
				...state.patientsLookup,
				[patientId]: {
					patient_id: patientId,
					first_name: firstName ?? "",
					last_name: lastName ?? "",
					code: data.code ?? "",
					title: data.title ?? "",
				},
			},
		}));
		return patientId;
	},
	pinPatient: (patient: PatientEntityType) => {
		if (!patient.id) return;
		set((state) => ({
			pinnedPatients: {
				...state.pinnedPatients,
				[patient.id as string]: patient,
			},
		}));
	},
	unpinPatient: (patientId: string) => {
		if (!patientId) return;
		set((state) => {
			const { [patientId]: _removed, ...rest } = state.pinnedPatients;
			return { pinnedPatients: rest };
		});
	},
	unpinAllPatients: () => {
		set({ pinnedPatients: {} });
	},
});
